/**
 * The ringing state machine for new website orders.
 *
 * Watches the order list, decides what is new (via the pure rules in
 * utils/orderAlert), and drives the repeating chime until somebody
 * acknowledges it, the cap runs out, or the shop closes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ALERT_ACTIVE_STATUSES,
  ALERT_RING_CAP_MS,
  ALERT_RING_INTERVAL_MS,
} from '../utils/constants';
import { selectAlertableOrders, shouldContinueRinging } from '../utils/orderAlert';
import { loadAlertedOrderIds, recordAlertedOrderIds } from '../utils/alertedOrders';
import { alertSound as sharedAlertSound } from '../utils/alertSound';

export default function useOrderAlarm({ orders, enabled = true, sound = sharedAlertSound }) {
  // Ids currently alarming. Empty means silent.
  const [alarmingIds, setAlarmingIds] = useState([]);
  // True once the cap or closing time has silenced a still-unacknowledged
  // alarm, so the banner can say so rather than pretending all is well.
  const [rungOut, setRungOut] = useState(false);

  const alertedIdsRef = useRef(null);
  // Pruning writes to localStorage, but it is idempotent, so doing it on the
  // first render (twice under StrictMode) is harmless.
  if (alertedIdsRef.current === null) alertedIdsRef.current = loadAlertedOrderIds();

  const ringDeadlineRef = useRef(0);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  // Detect new orders, and drop ones that have since been handled.
  useEffect(() => {
    if (!enabled) {
      // Logging out has to take the banner and the noise with it. Functional
      // updates keep the identity stable so this cannot loop.
      setAlarmingIds((previous) => (previous.length ? [] : previous));
      setRungOut(false);
      return;
    }

    const now = new Date();
    const fresh = selectAlertableOrders(orders, alertedIdsRef.current, now);

    if (fresh.length) {
      alertedIdsRef.current = recordAlertedOrderIds(
        fresh.map((order) => String(order.id)),
        now.getTime()
      );
      // A new arrival restarts the budget, so an order landing late in an
      // existing alarm still gets a full window of noise.
      ringDeadlineRef.current = now.getTime() + ALERT_RING_CAP_MS;
      setRungOut(false);
    }

    setAlarmingIds((previous) => {
      const kept = previous.filter((id) => {
        const order = orders.find((candidate) => String(candidate.id) === id);
        // Absent from this fetch is not proof it is gone — a partial refetch
        // must not silence a real order. Only an explicit status change does.
        if (!order) return true;
        return ALERT_ACTIVE_STATUSES.includes(order.status);
      });

      const additions = fresh
        .map((order) => String(order.id))
        .filter((id) => !kept.includes(id));
      const next = kept.concat(additions);

      // Preserve identity when nothing moved, so the ring effect below does not
      // tear down and restart the interval on every sync tick.
      const unchanged =
        next.length === previous.length && next.every((id, index) => id === previous[index]);
      return unchanged ? previous : next;
    });
  }, [orders, enabled]);

  const isRinging = alarmingIds.length > 0;

  // Drive the chime. Keyed only on whether anything is alarming, so orders
  // joining a running alarm do not restart the interval or stack audio.
  useEffect(() => {
    if (!isRinging) {
      soundRef.current?.stop?.();
      return undefined;
    }

    const ring = () => {
      soundRef.current?.play?.();
    };
    ring();

    const timer = setInterval(() => {
      // The cap and closing time are both re-checked on every repeat; see
      // shouldContinueRinging for why.
      if (!shouldContinueRinging({ now: Date.now(), deadline: ringDeadlineRef.current })) {
        clearInterval(timer);
        soundRef.current?.stop?.();
        // Stop the noise but leave the banner up. A missed order stays visible
        // even once the tablet has given up trying to be heard.
        setRungOut(true);
        return;
      }
      ring();
    }, ALERT_RING_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      soundRef.current?.stop?.();
    };
  }, [isRinging]);

  const acknowledge = useCallback(() => {
    soundRef.current?.stop?.();
    setAlarmingIds([]);
    setRungOut(false);
  }, []);

  const alarmingOrders = useMemo(
    () =>
      alarmingIds.map((id) => {
        const order = orders.find((candidate) => String(candidate.id) === id);
        if (order) return order;
        // Alarming but absent from the current fetch. Without a placeholder the
        // banner would render nothing while the chime kept going, leaving no way
        // to silence it. Details are omitted rather than guessed.
        return { id, isPlaceholder: true };
      }),
    [alarmingIds, orders]
  );

  return { alarmingOrders, isRinging, rungOut, acknowledge };
}
